/**
 * LLM Pricing Management
 * Admin interface for managing LLM model pricing
 *
 * Allows admins to view and update pricing for different LLM providers and models.
 * Pricing is per 1,000 tokens (input/output separately).
 */

import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../hooks/useToast';
import { API_BASE_URL } from '../../utils/constants';

interface ModelPricing {
  provider: string;
  model: string;
  prompt_per_1k: number;
  completion_per_1k: number;
  credit_cost: number;  // Number of credits consumed per API call
  supports_thinking: boolean;  // Whether this model supports extended thinking/reasoning
  is_fallback: boolean;  // Admin-designated fallback model for provider failover
  is_active: boolean;  // Whether this model is available for chat use
}

export const LLMPricing: React.FC = () => {
  const { admin } = useAdminAuth();
  const { toast } = useToast();
  const [pricing, setPricing] = useState<ModelPricing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingModel, setEditingModel] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<{
    provider: string;
    model: string;
    prompt_per_1k: number;
    completion_per_1k: number;
    credit_cost: number;
    supports_thinking: boolean;
    is_fallback: boolean;
  } | null>(null);
  const [newModel, setNewModel] = useState({
    provider: '',
    model: '',
    prompt_per_1k: '',
    completion_per_1k: '',
    credit_cost: '',
    supports_thinking: false
  });

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/llm-pricing`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPricing(data.pricing);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load pricing',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (
    oldProvider: string,
    oldModel: string,
    newProvider: string,
    newModel: string,
    promptPrice: number,
    completionPrice: number,
    creditCost: number,
    supportsThinking: boolean,
    isFallback: boolean = false
  ) => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/llm-pricing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          provider: newProvider,
          model: newModel,
          prompt_per_1k: promptPrice,
          completion_per_1k: completionPrice,
          credit_cost: creditCost,
          supports_thinking: supportsThinking,
          is_fallback: isFallback,
          old_provider: oldProvider,
          old_model: oldModel
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Pricing updated successfully',
          variant: 'success'
        });
        setEditingModel(null);
        setEditingValues(null);
        loadPricing();
      } else {
        const error = await response.json().catch(() => null);
        toast({
          title: 'Error',
          description: error?.detail || 'Failed to update pricing',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update pricing',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddModel = async () => {
    if (!newModel.provider || !newModel.model) {
      toast({
        title: 'Validation error',
        description: 'Provider and model are required',
        variant: 'destructive'
      });
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/llm-pricing/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          provider: newModel.provider.trim(),
          model: newModel.model.trim(),
          prompt_per_1k: parseFloat(newModel.prompt_per_1k || '0'),
          completion_per_1k: parseFloat(newModel.completion_per_1k || '0'),
          credit_cost: parseInt(newModel.credit_cost || '0'),
          supports_thinking: newModel.supports_thinking
        })
      });

      if (response.ok) {
        toast({
          title: 'Model added',
          description: `${newModel.provider}/${newModel.model} added successfully`,
          variant: 'success'
        });
        setNewModel({
          provider: '',
          model: '',
          prompt_per_1k: '',
          completion_per_1k: '',
          credit_cost: '',
          supports_thinking: false
        });
        loadPricing();
      } else {
        const error = await response.json().catch(() => null);
        toast({
          title: 'Error',
          description: error?.detail || 'Failed to add model',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add model',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (provider: string, model: string) => {
    const confirmDelete = window.confirm(`Deactivate pricing for ${provider}/${model}?`);
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/llm-pricing?provider=${encodeURIComponent(provider)}&model=${encodeURIComponent(model)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (response.ok) {
        toast({
          title: 'Model deactivated',
          description: `${provider}/${model} removed from pricing`,
          variant: 'success'
        });
        loadPricing();
      } else {
        const error = await response.json().catch(() => null);
        toast({
          title: 'Error',
          description: error?.detail || 'Failed to delete pricing',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete pricing',
        variant: 'destructive'
      });
    }
  };

  const formatPrice = (price: number) => {
    return `$${price.toFixed(6)}`;
  };

  const formatPricePerMillion = (pricePer1k: number) => {
    return `$${(pricePer1k * 1000).toFixed(2)}/1M`;
  };

  // Toggle active/inactive status for a model
  const handleToggleActive = async (item: ModelPricing) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/llm-pricing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          provider: item.provider,
          model: item.model,
          prompt_per_1k: item.prompt_per_1k,
          completion_per_1k: item.completion_per_1k,
          credit_cost: item.credit_cost,
          supports_thinking: item.supports_thinking,
          is_active: !item.is_active
        })
      });

      if (response.ok) {
        toast({
          title: item.is_active ? 'Model deactivated' : 'Model activated',
          description: `${item.provider}/${item.model} is now ${item.is_active ? 'inactive' : 'active'}`,
          variant: 'success'
        });
        loadPricing();
      } else {
        const error = await response.json().catch(() => null);
        toast({
          title: 'Error',
          description: error?.detail || 'Failed to toggle model status',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle model status',
        variant: 'destructive'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading pricing...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">LLM Pricing Management</h1>
          <p className="text-gray-600 mt-2">Manage pricing for different LLM providers and models</p>
        </div>

        <Card className="mb-8 p-6 bg-white shadow-md border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Add New Model</h2>
              <p className="text-sm text-gray-500">Provide pricing in USD per 1k tokens and assign a credit cost.</p>
            </div>
            <div className="text-xs text-gray-400 uppercase tracking-wide">
              Quick Entry
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
            <div className="space-y-1">
              <label className="text-xs uppercase text-gray-500 tracking-wide">Provider</label>
              <input
                type="text"
                placeholder="e.g. openai"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition"
                value={newModel.provider}
                onChange={(e) => setNewModel((prev) => ({ ...prev, provider: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase text-gray-500 tracking-wide">Model</label>
              <input
                type="text"
                placeholder="e.g. gpt-4o"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition"
                value={newModel.model}
                onChange={(e) => setNewModel((prev) => ({ ...prev, model: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase text-gray-500 tracking-wide">Input $ / 1k</label>
              <input
                type="number"
                step="0.000001"
                placeholder="0.000000"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition"
                value={newModel.prompt_per_1k}
                onChange={(e) => setNewModel((prev) => ({ ...prev, prompt_per_1k: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase text-gray-500 tracking-wide">Output $ / 1k</label>
              <input
                type="number"
                step="0.000001"
                placeholder="0.000000"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition"
                value={newModel.completion_per_1k}
                onChange={(e) => setNewModel((prev) => ({ ...prev, completion_per_1k: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase text-gray-500 tracking-wide">Credits</label>
              <input
                type="number"
                step="1"
                placeholder="10"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition"
                value={newModel.credit_cost}
                onChange={(e) => setNewModel((prev) => ({ ...prev, credit_cost: e.target.value }))}
              />
            </div>
            <div className="space-y-1 flex flex-col justify-end">
              <label className="text-xs uppercase text-gray-500 tracking-wide">Thinking</label>
              <label className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition">
                <input
                  type="checkbox"
                  checked={newModel.supports_thinking}
                  onChange={(e) => setNewModel((prev) => ({ ...prev, supports_thinking: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">Yes</span>
              </label>
            </div>
            <div className="flex items-end">
              <Button
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow"
                onClick={handleAddModel}
                disabled={isSaving}
              >
                Add Model
              </Button>
            </div>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Input Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Output Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credit Cost
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thinking
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fallback
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pricing.map((item) => {
                  const key = `${item.provider}:${item.model}`;
                  const isEditing = editingModel === key;

                  return (
                    <tr key={key} className={!item.is_active ? 'opacity-50 bg-gray-50' : ''}>
                      {/* Active toggle */}
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleToggleActive(item)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                            item.is_active ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                          title={item.is_active ? 'Click to deactivate' : 'Click to activate'}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                              item.is_active ? 'translate-x-4.5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-900">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editingValues?.provider || item.provider}
                            onChange={(e) => setEditingValues(prev => prev ? { ...prev, provider: e.target.value } : null)}
                            className="w-32 px-2 py-1 border border-gray-300 rounded uppercase"
                            placeholder="Provider"
                          />
                        ) : (
                          <span className="px-2 py-1 bg-blue-50 rounded-full text-xs uppercase tracking-wide">
                            {item.provider}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-mono">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editingValues?.model || item.model}
                            onChange={(e) => setEditingValues(prev => prev ? { ...prev, model: e.target.value } : null)}
                            className="w-48 px-2 py-1 border border-gray-300 rounded font-mono"
                            placeholder="Model name"
                          />
                        ) : (
                          item.model
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.000001"
                            value={editingValues?.prompt_per_1k ?? item.prompt_per_1k}
                            onChange={(e) => setEditingValues(prev => prev ? { ...prev, prompt_per_1k: parseFloat(e.target.value) || 0 } : null)}
                            className="w-32 px-2 py-1 border border-gray-300 rounded"
                          />
                        ) : (
                          <div>
                            <div className="font-medium">{formatPrice(item.prompt_per_1k)} / 1k</div>
                            <div className="text-xs text-gray-400">{formatPricePerMillion(item.prompt_per_1k)}</div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.000001"
                            value={editingValues?.completion_per_1k ?? item.completion_per_1k}
                            onChange={(e) => setEditingValues(prev => prev ? { ...prev, completion_per_1k: parseFloat(e.target.value) || 0 } : null)}
                            className="w-32 px-2 py-1 border border-gray-300 rounded"
                          />
                        ) : (
                          <div>
                            <div className="font-medium">{formatPrice(item.completion_per_1k)} / 1k</div>
                            <div className="text-xs text-gray-400">{formatPricePerMillion(item.completion_per_1k)}</div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {isEditing ? (
                          <input
                            type="number"
                            step="1"
                            value={editingValues?.credit_cost ?? item.credit_cost}
                            onChange={(e) => setEditingValues(prev => prev ? { ...prev, credit_cost: parseInt(e.target.value) || 0 } : null)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded"
                          />
                        ) : (
                          <span className="px-2 py-1 rounded-lg bg-purple-50 text-purple-700 font-semibold">
                            {item.credit_cost} credits
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                        {isEditing ? (
                          <input
                            type="checkbox"
                            checked={editingValues?.supports_thinking ?? item.supports_thinking}
                            onChange={(e) => setEditingValues(prev => prev ? { ...prev, supports_thinking: e.target.checked } : null)}
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                        ) : (
                          item.supports_thinking ? (
                            <span className="px-2 py-1 rounded-lg bg-green-50 text-green-700 text-xs font-semibold">Yes</span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                        {isEditing ? (
                          <input
                            type="checkbox"
                            checked={editingValues?.is_fallback ?? item.is_fallback}
                            onChange={(e) => setEditingValues(prev => prev ? { ...prev, is_fallback: e.target.checked } : null)}
                            className="h-4 w-4 text-orange-500 rounded focus:ring-orange-400"
                          />
                        ) : (
                          item.is_fallback ? (
                            <span className="px-2 py-1 rounded-lg bg-orange-50 text-orange-700 text-xs font-semibold">Fallback</span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {isEditing ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                if (editingValues) {
                                  handleUpdate(
                                    item.provider,  // old provider
                                    item.model,  // old model
                                    editingValues.provider,  // new provider
                                    editingValues.model,  // new model
                                    editingValues.prompt_per_1k,
                                    editingValues.completion_per_1k,
                                    editingValues.credit_cost,
                                    editingValues.supports_thinking,
                                    editingValues.is_fallback,
                                  );
                                }
                              }}
                              disabled={isSaving}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingModel(null);
                                setEditingValues(null);
                              }}
                              disabled={isSaving}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingModel(key);
                                setEditingValues({
                                  provider: item.provider,
                                  model: item.model,
                                  prompt_per_1k: item.prompt_per_1k,
                                  completion_per_1k: item.completion_per_1k,
                                  credit_cost: item.credit_cost,
                                  supports_thinking: item.supports_thinking,
                                  is_fallback: item.is_fallback,
                                });
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => handleDelete(item.provider, item.model)}
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Pricing Notes:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Prices are per 1,000 tokens (input and output separately)</li>
            <li>• Credit Cost determines how many credits are consumed per API call for this model</li>
            <li>• Higher credit costs for expensive models (e.g., gpt-4o=20, gpt-4o-mini=10, gemini=1)</li>
            <li>• <strong>Active models</strong> appear in user model selectors and are used for chat</li>
            <li>• <strong>Inactive models</strong> are hidden from users and cannot be used for chat (useful for embedding/analysis-only models)</li>
            <li>• If a bot uses an inactive model, it falls back to the default model</li>
            <li>• <strong>Fallback model</strong>: Mark one active model as "Fallback" — it auto-activates when the primary provider is down</li>
            <li>• Changes take effect immediately for new LLM calls</li>
            <li>• Historical usage costs are not recalculated</li>
            <li>• Verify prices against official provider documentation</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
